<template>
  <div v-if="visible" :class="$style.containerClass">
    <div
      v-if="visibility.overlay"
      :class="$style.overlay"
      :data-modal="name"
      :aria-expanded="visibility.overlay.toString()"
      @click.self.stop="onOverlayClick"
    ></div>
    <div
      v-if="visibility.modal"
      ref="modal"
      :aria-expanded="visibility.modal.toString()"
      :class="$style.modal"
      role="dialog"
      aria-modal="true"
    >
      <slot />
    </div>
  </div>
</template>

<script>
import { blurActiveElement } from "../../utils/dom/dom-util";

export default {
  name: "modal",
  props: {
    name: { type: String, required: true },
    clickToClose: { type: Boolean, default: false }
  },
  data() {
    return {
      visible: false,
      visibility: {
        modal: false,
        overlay: false
      }
    };
  },
  /**
   * Sets global listeners
   */
  beforeMount() {
    this.$bvmModal.subscription.$on("toggle", this.onToggle);

    if (this.clickToClose) {
      window.addEventListener("keyup", this.onEscapeKeyUp);
    }
  },
  /**
   * Removes global listeners
   */
  beforeDestroy() {
    this.$bvmModal.subscription.$off("toggle", this.onToggle);

    if (this.clickToClose) {
      window.removeEventListener("keyup", this.onEscapeKeyUp);
    }
  },
  methods: {
    startTransitionEnter() {
      this.visibility.overlay = true;
      this.visibility.modal = true;
    },
    startTransitionLeave() {
      this.visibility.overlay = false;
      this.visibility.modal = false;

      const event = this.createModalEvent({
        state: "closed"
      });
      this.$emit("closed", event);
    },
    open(params) {
      let cancelEvent = false;
      const cancel = () => {
        cancelEvent = true;
      };

      const event = this.createModalEvent({
        cancel,
        state: "before-open",
        params
      });

      this.$emit("before-open", event);

      if (cancelEvent) {
        return;
      }

      // Need to unfocus previously focused element, otherwise
      // all keypress events (ESC press, for example) will trigger on that element.
      blurActiveElement();

      this.visible = true;

      // Making sure that entering transition uses "enter" sequence instead of "appear"
      this.$nextTick(() => {
        this.startTransitionEnter();
      });
    },
    close(params) {
      let cancelEvent = false;
      const cancel = () => {
        cancelEvent = true;
      };

      const event = this.createModalEvent({
        cancel,
        state: "before-close",
        params
      });

      this.$emit("before-close", event);

      if (cancelEvent) {
        return;
      }

      this.visible = false;

      this.startTransitionLeave();
    },
    toggle(isOpening, params) {
      const { visible } = this;

      if (visible === isOpening) {
        return;
      }

      if (isOpening) {
        this.open(params);
      } else {
        this.close(params);
      }
    },
    /**
     * Generates event object
     */
    createModalEvent(properties = {}) {
      return {
        name: this.name,
        ref: this.$refs.modal || null,
        ...properties
      };
    },
    // -----------------
    // On events
    /**
     * Event handler that is triggered when a key is pressed
     */
    onEscapeKeyUp(event) {
      if (event.which === 27 && this.visible) {
        this.$bvmModal.hide(this.name);
      }
    },
    /**
     * Event handler that is triggered when background overlay is clicked
     */
    onOverlayClick() {
      if (this.clickToClose) {
        this.toggle(false);
      }
    },
    /**
     * Event handler that is triggered when the "toggle" event is emitted
     */
    onToggle(name, state, params) {
      if (this.name === name) {
        const nextState = typeof state === "undefined" ? !this.visible : state;
        this.toggle(nextState, params);
      }
    }
  }
};
</script>

<style module lang="scss">
.containerClass {
  position: fixed;
  box-sizing: border-box;
  left: 0;
  top: 0;
  width: 100%;
  height: 100vh;
  z-index: 999;
}

.overlay {
  position: fixed;
  box-sizing: border-box;
  left: 0;
  top: 0;
  width: 100%;
  height: 100vh;
  background: rgba(0, 0, 0, 0.2);
  opacity: 1;
}
</style>
